"use client";
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Textarea, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogClose, DialogTitle, DialogDescription, Tooltip, TooltipProvider, TooltipTrigger, TooltipContent, useToast, Toaster } from '@postcheesy/ui-lib/molecules';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Heart, Bell, Trash2, Pencil, HeartCrack, Lock } from 'lucide-react';
import { Button } from '@postcheesy/ui-lib/atoms';
import { CheeseItemId } from '@/src/api/getCheeseById';
import { useNavigate } from "react-router-dom";
import { useCheeses } from '../../api/getCheeses';
import { addObservation, deleteObservation } from '../../api/observation';
import updateCheeseFlags, { Flags } from '../../api/cheeseFlags';
import { useCheeseById } from '../../api/getCheeseById';
import { useCheeseImage } from '../../api/getCheeseImage';


const ProductCard: React.FC = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [comment, setComment] = useState('');
    const [liked, setLiked] = useState(false);
    const [notified, setNotified] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const [notes, setNotes] = useState<{ id: number, text: string }[]>([]);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [tempNotes, setTempNotes] = useState<number[]>([]);
    const { toast } = useToast();
    const apiUrl = import.meta.env.VITE_API_URL;

    const { data: cheeses = [] } = useCheeses();
    const boughtProducts = cheeses?.map(cheese => cheese.id) || [];

    const { data: product } = useCheeseById(Number(slug));
    const { data: imageBlob } = useCheeseImage(Number(slug), 300, 300);
    const [productImage, setProductImage] = useState<string | null>(null);

    const handleComment = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!comment.trim()) return;

        setIsAddingNote(true);
        const tempId = Date.now();
        setTempNotes(prev => [...prev, tempId]);

        setNotes(prevNotes => [
            { id: tempId, text: comment },
            ...prevNotes
        ]);
        setComment('');

        try {
            const response = await addObservation(Number(slug), comment);
            if (response) {
                setNotes(prevNotes => [
                    { id: response.id, text: comment },
                    ...prevNotes.filter(note => note.id !== tempId)
                ]);
                setTempNotes(prev => prev.filter(id => id !== tempId));
            }
        } catch (error) {
            setNotes(prevNotes => prevNotes.filter(note => note.id !== tempId));
            setTempNotes(prev => prev.filter(id => id !== tempId));
            toast({
                title: "Erreur",
                description: "Échec de l'ajout de la note",
                variant: "destructive",
            });
        } finally {
            setIsAddingNote(false);
            document.getElementById('closeDialog')?.click();
        }
    };
    const handleDeleteNote = async (index: number, observationId: number) => {
        if (tempNotes.includes(observationId)) {
            setNotes(prevNotes => prevNotes.filter(note => note.id !== observationId));
            setTempNotes(prev => prev.filter(id => id !== observationId));
            return;
        }

        try {
            const response = await deleteObservation(Number(slug), observationId);
            if (response) {
                setNotes(prevNotes => prevNotes.filter(note => note.id !== observationId));
                toast({
                    title: "Note supprimée",
                    description: "Votre note a été supprimée",
                });
            }
        } catch (error) {
            toast({
                title: "Erreur",
                description: "Échec de la suppression de la note",
                variant: "destructive",
            });
        }
    };
    const handleFlagChange = async (flag: 'liked' | 'disliked' | 'notified', value: boolean) => {
        if (flag === 'liked') {
            setLiked(value);
            setDisliked(false);
        }
        else if (flag === 'disliked') {
            setDisliked(value);
            setLiked(false);
        }
        else if (flag === 'notified') {
            setNotified(value);
        }

        const newFlags : Flags = (() => {
            switch (flag) {
                case 'liked':
                    return {
                        favorite: value,
                        disliked: value === true ? false : undefined,
                    };
                case 'disliked':
                    return {
                        favorite: value === true ? false : undefined,
                        disliked: value,
                    };
                case 'notified':
                    return {
                        subscribed: value,
                    };
                default: return {};
            }
        })();

        const fromageId = product?.id;
        if (!fromageId) return;

        try {
            const success = await updateCheeseFlags(fromageId, newFlags);
            if (!success) {
                if (flag === 'liked') {
                    setLiked(!value);
                }
                else if (flag === 'disliked') {
                    setDisliked(!value);
                }
                toast({
                    title: "Échec de la mise à jour",
                    description: "Impossible de mettre à jour vos préférences",
                });
            }
        } catch (error) {
            if (flag === 'liked') {
                setLiked(!value);
            }
            else if (flag === 'disliked') {
                setDisliked(!value);
            }
            toast({
                title: "Erreur",
                description: "Une erreur s'est produite",
            });
        }
    };
    useEffect(() => {
        if (imageBlob) {
            const url = URL.createObjectURL(imageBlob);
            setProductImage(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [imageBlob]);

    useEffect(() => {
        if (product) {
            setLiked(product.favorite);
            setDisliked(product.disliked);
            setNotified(product.subscribed);
            setNotes(product.observations.map(o => ({ id: o.observationId, text: o.observation })));
        }
    }, [product]);

    return (
        <div>
            <Toaster />
            <div className='py-4'>
                <h2 className='text-4xl serif text-foreground py-2 font-semibold'>{product?.fromageNom}</h2>
            </div>
            <Card>
                <CardHeader>
                </CardHeader>
                <CardContent className="flex flex-col md:flex-row gap-4">
                    <div className={`w-[300px] h-[300px] relative`} >
                        {productImage ? (
                            <img src={productImage} alt="Cheese" />
                        ) : (
                            <div className="bg-gray-200 rounded-md h-full w-full flex items-center justify-center" />
                        )}
                    </div>
                    <CardDescription className='flex flex-col gap-4'>
                        <div className="w-full flex gap-2">
                            <Heart size={35} color='#870037' onClick={() => handleFlagChange("liked", !liked)}
                                className={`cursor-pointer transition-transform duration-200 ${liked ? "fill-red-500 scale-110" : "fill-transparent"
                                    }`}
                            />
                            <HeartCrack size={35} color='#870037' onClick={() => handleFlagChange("disliked", !disliked)}
                                className={`cursor-pointer transition-transform duration-200 ${disliked ? "fill-red-500 scale-110" : "fill-transparent"
                                    }`}
                            />
                            <Bell size={35} color='#870037'
                                onClick={() => {
                                    handleFlagChange("notified", !notified);
                                    toast({
                                        title: notified
                                            ? "Vous ne serez plus notifié à propos de ce fromage."
                                            : "Vous serez notifié à propos de ce fromage.",
                                    });
                                }}
                                className={`cursor-pointer transition-transform duration-200 ${notified ? "fill-red-500 scale-110" : "fill-transparent"
                                    }`}
                            />
                        </div>
                        <div className='text-foreground text-[16px] tracking-wider leading-6'>
                            <strong>Lait : </strong>{product?.laitDescription}<br />
                            <strong>Famille : </strong>{product?.familleDescription}<br />
                            <strong>Traitement : </strong>{product?.traitementDescription}<br />
                            <strong>Pays : </strong>{product?.paysDescription}<br />
                            <strong>Région : </strong>{product?.regionDescription}<br />
                            <strong>Date du dernier achat : </strong>{product?.dateDernierAchat ? format(new Date(product.dateDernierAchat), 'yyyy/MM/dd') : ''}<br />
                        </div>
                    </CardDescription>
                </CardContent>
                <CardContent>
                    <p className='text-justify'>{product?.fromageDescription}</p>
                </CardContent>
                {product?.comparables && product.comparables.length > 0 && (
                    <CardContent className="flex flex-col gap-4">
                        <CardTitle>Plus comme le {product?.fromageNom}</CardTitle>
                        <ul className="flex flex-col gap-4 md:flex-row">
                            {product?.comparables.map((product, index) => (
                                <li key={index} className='md:w-1/3'>
                                    {product.clickable && boughtProducts.includes(product.id) ?
                                        <button className='w-full' onClick={() => { navigate(`/product/${product.id}`) }}>
                                            <Card className='flex gap-4 items-center p-2 border-foreground'>
                                                <div className={`w-[100px] h-[100px] relative`} >
                                                    <img
                                                        src={`${apiUrl}/fromage/${product.id}/image?x=100&y=100`}
                                                        alt={product.fromageNom}
                                                        onError={(e) => { e.currentTarget.src = `${apiUrl}/fromage/1/image?x=100&y=100` }}
                                                        loading='lazy'
                                                    />
                                                </div>
                                                <CardDescription className='text-foreground'>{product?.fromageNom}</CardDescription>
                                            </Card>
                                        </button>
                                        :
                                        <Card className='flex items-center justify-between p-2'>
                                            <div className='flex items-center justify-center gap-4'>
                                                <div className="w-[100px] h-[100px] relative">
                                                    <img
                                                        src={`${apiUrl}/fromage/${product.id}/image?x=100&y=100`}
                                                        alt={product.fromageNom}
                                                        onError={(e) => { e.currentTarget.src = `${apiUrl}/fromage/1/image?x=100&y=100` }}
                                                    />
                                                </div>
                                                <CardDescription>{product?.fromageNom}</CardDescription>
                                            </div>
                                            <Lock size={20} style={{ color: '#870037' }} />
                                        </Card>
                                    }
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                )}
                {product?.accords && product.accords.length > 0 && (
                    <CardContent className="flex flex-col gap-4">
                        <CardTitle>Nos accords proposés</CardTitle>
                        <ul className="flex flex-col gap-4 md:flex-row">
                            {product?.accords.map((accord, index) => (
                                <li key={index} className='md:w-1/3'>
                                    <Card className='flex gap-4 items-center p-2'>
                                        <div className={`min-w-[100px] min-h-[100px] relative overflow-clip`} >
                                            <img
                                                src={`${apiUrl}/accord/${accord.accordId}/image?x=100&y=100`}
                                                alt={accord.accordNom}
                                                onError={(e) => { e.currentTarget.src = `${apiUrl}/accord/1/image?x=100&y=100` }}
                                                loading='lazy'
                                            />
                                        </div>
                                        <CardDescription className='flex flex-col gap-2'>
                                            <div className='text-foreground'>{accord?.accordNom}</div>
                                            <div className='flex-1 justify-between'>
                                                <p>{accord?.typeAccordDescription && 'Type : '}{accord?.typeAccordDescription}</p>
                                                <p>{accord?.typeAccordCategorieDescription && 'Catégorie : '}{accord?.typeAccordCategorieDescription}</p>
                                                <p>{accord?.origineDescription && 'Origine : '}{accord?.origineDescription}</p>
                                                <p>{accord?.numeroSaq && 'Saq : '}{accord?.numeroSaq}</p>
                                            </div>

                                        </CardDescription>

                                    </Card>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                )}
                <CardContent>
                    <CardTitle className='pb-4'>Notes</CardTitle>
                    <CardDescription>
                        Vos notes restent privées.
                    </CardDescription>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 pb-4" key={notes.length}>
                        {notes.map((note, index) => (
                            <Card key={note.id} className="flex gap-4 items-center p-2">
                                <CardDescription className='w-full flex justify-between'>
                                    <p className='w-full whitespace-pre-wrap break-words'>{note.text}</p>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <Button
                                                    variant={'outline'}
                                                    size={'icon'}
                                                    onClick={() => handleDeleteNote(index, note.id)}
                                                >
                                                    <Trash2 size={20} />
                                                </Button>
                                                <TooltipContent>
                                                    <p>Supprimer est irréversible</p>
                                                </TooltipContent>
                                            </TooltipTrigger>
                                        </Tooltip>
                                    </TooltipProvider>
                                </CardDescription>
                            </Card>
                        ))}
                    </div>
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button onClick={() => setComment('')}>
                                <div className='flex gap-2 items-center'>
                                    Ajouter une Note <Pencil size={20} />
                                </div>
                            </Button>
                        </DialogTrigger>
                        <DialogContent
                            className="overflow-y-auto sm:top-[50%] max-sm:bottom-0 max-sm:top-auto max-sm:translate-y-0 max-sm:rounded-b-none max-sm:w-full"
                            onInteractOutside={(e) => {
                                const target = e.target as HTMLElement;
                                if (target.classList.contains('mobile-keyboard')) {
                                    e.preventDefault();
                                }
                            }}
                        >
                            <DialogHeader>
                                <DialogTitle className='pb-4'>Ajouter une note personnelle</DialogTitle>
                                <DialogDescription>
                                    <form onSubmit={handleComment} className='flex flex-col gap-4 pb-4'>
                                        <Textarea
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            placeholder="Votre commentaire"
                                            className="w-full text-base"
                                            required
                                            disabled={isAddingNote}
                                        />
                                        <div className="flex gap-2">
                                            <DialogClose id="closeDialog" asChild>
                                                <Button type="button" variant="outline" disabled={isAddingNote}>
                                                    Annuler
                                                </Button>
                                            </DialogClose>
                                            <DialogClose>
                                                <Button
                                                    type="submit"
                                                    disabled={isAddingNote || !comment.trim()}
                                                    className="flex-1"
                                                >
                                                    {isAddingNote ? "En cours..." : "Ajouter"}
                                                </Button>
                                            </DialogClose>
                                        </div>
                                    </form>
                                </DialogDescription>
                            </DialogHeader>
                        </DialogContent>
                    </Dialog>
                </CardContent>
            </Card >
        </div >

    )
}

export default ProductCard;

